/**
 * MyShopMap — Interactive Leaflet map showing every published MyShop with
 * coordinates. Each pin is a flag-style marker with the shop name printed
 * above the drop pin so users can identify boutiques at a glance.
 *
 * v50.3 — Redesigned pins from a plain dot to a proper flag/drop-pin with
 * a persistent name label. Removed the prominent "Ma position" button;
 * geolocation is now requested silently on mount, with a discreet icon-only
 * recenter button in the top-right corner of the map for users who scrolled
 * away from their location. All permission/error messages are fully i18n.
 */
import React, { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useNavigate } from 'react-router-dom';
import { Loader2, LocateFixed, Store } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// Distinctive colour per category (canonical id used server-side).
const CATEGORY_COLORS = {
  fashion:     '#ec4899', // pink
  beauty:      '#f472b6', // rose
  myfood:      '#f59e0b', // amber
  art:         '#a855f7', // purple
  music:       '#8b5cf6', // violet
  handcraft:   '#84cc16', // lime
  electronics: '#3b82f6', // blue
  home:        '#06b6d4', // cyan
  wellness:    '#22c55e', // green
  services:    '#64748b', // slate
  education:   '#f97316', // orange
  kids:        '#eab308', // yellow
  sport:       '#dc2626', // red
  jewelry:     '#facc15', // gold
  books:       '#a16207', // brown
  other:       '#9ca3af', // gray
};

const escapeHtml = (str) => String(str || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

// Flag-style pin: drop-pin marker with a persistent name label above.
const iconFor = (shop) => {
  const color = CATEGORY_COLORS[shop.category] || CATEGORY_COLORS.other;
  const rawName = shop.name || '';
  const short = rawName.length > 22 ? `${rawName.slice(0, 20)}…` : rawName;
  const name = escapeHtml(short);
  return L.divIcon({
    className: 'mymap-flag-pin',
    html: `
      <div style="position:relative; width:26px; height:34px;">
        <div style="
          position:absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          margin-bottom: 3px;
          background: rgba(0,0,0,.85);
          color: #fff;
          font-size: 10px;
          font-weight: 600;
          line-height: 1;
          padding: 3px 7px;
          border-radius: 8px;
          white-space: nowrap;
          border: 1px solid ${color};
          box-shadow: 0 2px 6px rgba(0,0,0,.45);
          pointer-events: none;
        ">${name}</div>
        <svg width="26" height="34" viewBox="0 0 26 34" xmlns="http://www.w3.org/2000/svg" style="display:block; filter: drop-shadow(0 3px 5px rgba(0,0,0,.5));">
          <path d="M13 0C5.8 0 0 5.8 0 13c0 8.7 13 21 13 21s13-12.3 13-21C26 5.8 20.2 0 13 0z" fill="${color}" stroke="#ffffff" stroke-width="2"/>
          <circle cx="13" cy="13" r="4.5" fill="#ffffff"/>
        </svg>
      </div>
    `,
    iconSize: [26, 34],
    iconAnchor: [13, 34],
    popupAnchor: [0, -34],
  });
};

// Helper component that recentres the map when the user location is obtained.
const RecenterOnUser = ({ userPos, trigger }) => {
  const map = useMap();
  useEffect(() => {
    if (userPos) {
      try { map.setView([userPos.lat, userPos.lng], 13); } catch (_) { /* no-op */ }
    }
  }, [userPos, trigger, map]);
  return null;
};

const MyShopMap = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userPos, setUserPos] = useState(null);
  const [locError, setLocError] = useState(null);
  const [locPending, setLocPending] = useState(false);
  const [recenterTrigger, setRecenterTrigger] = useState(0);
  const askedGeoRef = useRef(false);

  // Fetch pins once on mount.
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get('/api/shop/map?limit=500');
        if (alive) setShops(r.data?.shops || []);
      } catch (_) {
        if (alive) setShops([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Ask geolocation once (silent, non-blocking).
  const requestGeo = (fromButton = false) => {
    if (!fromButton && askedGeoRef.current) return;
    askedGeoRef.current = true;
    setLocError(null);
    if (!navigator?.geolocation) {
      setLocError(t('mymap.geoUnsupported', 'Géolocalisation non supportée'));
      return;
    }
    setLocPending(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserPos({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocPending(false);
        setRecenterTrigger(x => x + 1);
      },
      (err) => {
        setLocError(err?.code === 1
          ? t('mymap.geoDenied', "Autorisation refusée — la carte s'affiche en vue globale.")
          : t('mymap.geoError', "Impossible d'obtenir votre position."));
        setLocPending(false);
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
    );
  };

  // Silently auto-request geolocation on first render (browsers still prompt).
  useEffect(() => {
    requestGeo(false);
  }, []);

  // Legend built from categories actually present on the map.
  const legend = useMemo(() => {
    const seen = new Set();
    shops.forEach(s => { if (s.category) seen.add(s.category); });
    return Array.from(seen).sort();
  }, [shops]);

  const initialCenter = userPos
    ? [userPos.lat, userPos.lng]
    : (shops[0] && shops[0].lat ? [shops[0].lat, shops[0].lng] : [4.9, -52.3]); // French Guiana default
  const initialZoom = userPos ? 13 : (shops.length > 0 ? 5 : 3);

  return (
    <div data-testid="mymap-container" className="space-y-3">
      <div className="flex items-center justify-between px-1">
        <div className="text-sm font-semibold text-white/80">
          {loading
            ? t('mymap.loading', 'Chargement des boutiques…')
            : t('mymap.count', '{n} boutiques localisées').replace('{n}', shops.length)
          }
        </div>
      </div>

      {locError && (
        <div className="text-[11px] text-amber-300 bg-amber-500/10 border border-amber-400/30 rounded-lg px-3 py-2" data-testid="mymap-loc-error">
          {locError}
        </div>
      )}

      <div className="relative rounded-2xl overflow-hidden border border-white/10" style={{ height: '55vh', minHeight: 380 }}>
        {loading ? (
          <div className="w-full h-full flex items-center justify-center bg-black/40">
            <Loader2 className="w-6 h-6 animate-spin text-fuchsia-400" />
          </div>
        ) : (
          <>
            <MapContainer center={initialCenter} zoom={initialZoom} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a>'
              />
              <RecenterOnUser userPos={userPos} trigger={recenterTrigger} />
              {userPos && (
                <Marker
                  position={[userPos.lat, userPos.lng]}
                  icon={L.divIcon({
                    className: 'mymap-user',
                    html: '<div style="width:14px;height:14px;border-radius:50%;background:#22d3ee;border:3px solid #fff;box-shadow:0 0 0 4px rgba(34,211,238,.3)"></div>',
                    iconSize: [14, 14],
                    iconAnchor: [7, 7],
                  })}
                />
              )}
              {shops.map((s) => (
                <Marker
                  key={s.id}
                  position={[s.lat, s.lng]}
                  icon={iconFor(s)}
                  eventHandlers={{ click: () => navigate(`/shop/${s.id}`) }}
                >
                  <Popup>
                    <div className="text-sm min-w-[140px]">
                      <div className="font-semibold text-gray-900">{s.name}</div>
                      <div className="text-[11px] text-gray-600 uppercase tracking-wide mb-2">
                        {t(`eshop.cat_${s.category}`) || s.category}
                      </div>
                      <button
                        onClick={() => navigate(`/shop/${s.id}`)}
                        className="w-full px-3 py-1.5 rounded-full bg-fuchsia-500 hover:bg-fuchsia-600 text-white text-xs font-semibold"
                        data-testid={`mymap-open-shop-${s.id}`}
                      >
                        {t('mymap.openShop', 'Ouvrir la boutique')}
                      </button>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>

            {/* Discreet recenter icon (top-right corner over the map) */}
            <button
              onClick={() => requestGeo(true)}
              disabled={locPending}
              title={t('mymap.recenter', 'Recentrer sur ma position')}
              aria-label={t('mymap.recenter', 'Recentrer sur ma position')}
              className="absolute top-3 right-3 z-[500] w-10 h-10 rounded-full bg-black/70 hover:bg-black/90 backdrop-blur-md border border-white/20 flex items-center justify-center text-white shadow-lg disabled:opacity-60"
              data-testid="mymap-recenter-btn"
            >
              {locPending
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <LocateFixed className="w-4 h-4" />
              }
            </button>
          </>
        )}
      </div>

      {/* Category legend */}
      {legend.length > 0 && (
        <div className="rounded-2xl bg-white/5 border border-white/10 p-3" data-testid="mymap-legend">
          <div className="text-[11px] text-white/50 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Store className="w-3 h-3" /> {t('mymap.legend', 'Légende')}
          </div>
          <div className="flex flex-wrap gap-2">
            {legend.map((cat) => (
              <div key={cat} className="flex items-center gap-1.5 text-[11px] text-white/80 bg-black/30 rounded-full px-2 py-1">
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ background: CATEGORY_COLORS[cat] || CATEGORY_COLORS.other }}
                />
                {t(`eshop.cat_${cat}`) || cat}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MyShopMap;
